// ═══════════════════════════════════════════════════════════════════
// TMS GAMMA — Configuração de Filiais e Filtros
// Módulos por filial: Dashboard · Tabelas · Conferência · Ocorrências
//                    Auditoria · Notas Fiscais · Análise
// Módulos globais:   Clientes · Municípios · Usuários
// ═══════════════════════════════════════════════════════════════════
// A lista de transportadoras NAO e importada aqui: ela ja chega como
// parametro de filtrarPorFilial, vinda do Supabase. O import estatico
// colocava as 52 transportadoras reais no bundle inicial, baixavel sem
// login, porque filiais.js e carregado pelo App e pela Sidebar antes da
// autenticacao (#32).

import { T } from "../theme/tokens";

// Cor de cada filial, lida do tema na hora (#216). Havia quatro cópias desta
// tabela (App, FilialBadge, Transportadoras e Sidebar), com HEX fixo e
// Curitiba no vermelho Gamma — que no resto do sistema é problema (#301).
// Aceita o nome ("Curitiba"), o código ("CWB"), o estado ("PR") e o rótulo
// de FILIAL_CFG ("Curitiba (CWB)", "PR — Curitiba, ..."). O rótulo é o que
// Ocorrências passa ao FilialBadge; ninguém o achava, e o selo caía na cor
// de reserva.
export function corFilial(f) {
  const cores = {
    Curitiba: T.cyan, CWB: T.cyan, PR: T.cyan,
    Maringá: T.purple, MGA: T.purple,
    Cascavel: T.green, CVL: T.green,
    "Ribeirão Preto": T.amber, RBP: T.amber, SP: T.amber,
    Marília: T.gray, MRL: T.gray,
  };
  const s = String(f || "");
  const chave = s in cores ? s : s.split(/ [(—]/)[0];
  return cores[chave] || T.gray;
}

// Mapeamento filial selecionada → filtros
// ocFiliais usa a mesma numeração das notas (01 · 03 · 04 · 05 · 06): é o
// que o banco grava em ocorrencias.filial. Dizia 01..05, e o filtro de
// Maringá não achava nada e o de Cascavel mostrava as de Maringá (#317).
export const FILIAL_CFG = {
  "todos":          { transpFiliais: null,                     munFiliais: null,              ocFiliais: null,              label:"Todas as filiais" },
  "PR":             { transpFiliais: ["CWB","MGA","CVL"],      munFiliais: ["01","03","04"],   ocFiliais: ["01","03","04"],   label:"PR — Curitiba, Maringá e Cascavel" },
  "Curitiba":       { transpFiliais: ["CWB"],                  munFiliais: ["01"],             ocFiliais: ["01"],            label:"Curitiba (CWB)" },
  "Maringá":        { transpFiliais: ["MGA"],                  munFiliais: ["03"],             ocFiliais: ["03"],            label:"Maringá (MGA)" },
  "Cascavel":       { transpFiliais: ["CVL"],                  munFiliais: ["04"],             ocFiliais: ["04"],            label:"Cascavel (CVL)" },
  "SP":             { transpFiliais: ["RBP","MRL"],            munFiliais: ["05","06"],        ocFiliais: ["05","06"],        label:"SP — Ribeirão Preto e Marília" },
  "Ribeirão Preto": { transpFiliais: ["RBP"],                  munFiliais: ["05"],             ocFiliais: ["05"],            label:"Ribeirão Preto (RBP)" },
  "Marília":        { transpFiliais: ["MRL"],                  munFiliais: ["06"],             ocFiliais: ["06"],            label:"Marília (MRL)" },
};

// CNPJ de cada filial da Gamma (notas_fiscais.filial). Remetente das NF-e e
// interessado nos CT-e da SEFAZ (#342). Era uma cópia local na AuditoriaCTe.
export const CNPJ_DA_FILIAL = {
  "01": "15545788000199",  // Curitiba
  "03": "15545788000350",  // Maringá
  "04": "15545788000431",  // Cascavel
  "05": "15545788000512",  // Ribeirão Preto
  "06": "15545788000601",  // Marília
};

// ── Filiais de acesso do usuário (#315) ───────────────────────────────
// O administrador marca, por usuário, quais das cinco cidades ele vê.
// Nenhuma marcada = todas. Na Lubpress vale o estado das cidades marcadas.
export const CIDADES = ["Curitiba", "Maringá", "Cascavel", "Ribeirão Preto", "Marília"];
const UF_DA_CIDADE = { Curitiba:"PR", Maringá:"PR", Cascavel:"PR", "Ribeirão Preto":"SP", Marília:"SP" };

/** Cidades que o usuário vê, na ordem de CIDADES; null = todas. */
export function filiaisDoUsuario({ cargo, filiais } = {}) {
  if (cargo === "admin" || !filiais?.length) return null;
  return CIDADES.filter(c => filiais.includes(c));
}

export const ufsDasFiliais = (cidades) => [...new Set(cidades.map(c => UF_DA_CIDADE[c]))];

// Várias cidades num filtro só: o "todas" de quem tem acesso a parte delas.
function unirFiliais(lista) {
  const junta = (k) => lista.flatMap(f => FILIAL_CFG[f]?.[k] ?? []);
  return { transpFiliais: junta("transpFiliais"), munFiliais: junta("munFiliais"),
    ocFiliais: junta("ocFiliais"), label: lista.join(", ") };
}

// `filialSel` é uma chave de FILIAL_CFG ou uma lista de cidades (#315).
export function filtrarPorFilial(filialSel, { transportadoras, municipios, ocorrencias, tabelas, notas }) {
  const cfg = Array.isArray(filialSel)
    ? unirFiliais(filialSel)
    : FILIAL_CFG[filialSel] || FILIAL_CFG["todos"];
  const t_  = transportadoras || [];
  const m_  = municipios      || [];
  const o_  = ocorrencias     || [];
  const tb_ = tabelas         || [];
  const n_  = notas           || [];

  return {
    transportadoras: cfg.transpFiliais
      ? t_.filter(t => cfg.transpFiliais.includes(t.filial))
      : t_,
    municipios: cfg.munFiliais
      ? m_.filter(m => cfg.munFiliais.includes(String(m.filial)))
      : m_,
    ocorrencias: cfg.ocFiliais
      ? o_.filter(o => cfg.ocFiliais.includes(String(o.filial)))
      : o_,
    tabelas: cfg.transpFiliais
      // t_ e a lista que a propria funcao recebeu. Antes isto ignorava o
      // parametro e lia a constante estatica — duas fontes para o mesmo
      // dado, exatamente o que o AGENTS.md §6 proibe.
      ? tb_.filter(t => cfg.transpFiliais.includes(
          (t_.find(x => x.cod === t.codTransp) || {}).filial || ""
        ))
      : tb_,
    notas: cfg.munFiliais
  ? (notas || []).filter(n => {
      // Usa filial direta da nota (importada do Excel)
      if (n.filial) return cfg.munFiliais.includes(String(n.filial).padStart(2,"0"));
      // Fallback para notas de exemplo (sem campo filial)
      const mun = m_.find(m => m.mun === n.municipio && m.uf === n.uf)
               || m_.find(m => m.mun === n.municipio);
      return cfg.munFiliais.includes(String(mun?.filial || ""));
    })
  : (notas || []),
  };
}
